/**
 * CONTRACT MANAGER — client-side price maths (Lee, 6 Oct 2026).
 * MIRROR of packages/api/src/crm/contracts/priceBook.ts `compute()` so the
 * builder preview and (Phase 2) the customer page recalc instantly without a
 * round-trip. The server recomputes on save/sign — it is the authority; keep
 * the two in sync when the pricing model changes.
 */
import type { CtPriceBook, CtSelections } from './crmApi'

const r2 = (n: number) => Math.round(n * 100) / 100

export interface CtLine { key: string; label: string; qty: number; unitMonthly: number; standardMonthly: number; discountPct: number; yourMonthly: number }
export interface CtComputed {
  lines: CtLine[]; discountPct: number; standardMonthly: number; monthlyTotal: number
  billedAnnually: number; savingPerMonth: number; savingOverTerm: number
  training: { key: string; label: string; fee: number }
  matrix: Array<{ termMonths: number; monthly: { pct: number; total: number }; annual: { pct: number; total: number; perYear: number } }>
}

export function ctCompute(book: CtPriceBook, sel: CtSelections): CtComputed {
  const lines: CtLine[] = []
  const pkg = book.packages[sel.package]
  const pct = book.discounts[String(sel.termMonths)]?.[sel.frequency] ?? 0
  const add = (key: string, label: string, qty: number, unit: number) => {
    if (qty <= 0) return
    const std = r2(unit * qty)
    lines.push({ key, label, qty, unitMonthly: unit, standardMonthly: std, discountPct: pct, yourMonthly: r2(std * (1 - pct / 100)) })
  }
  if (pkg) add(`package:${sel.package}`, pkg.label, 1, pkg.monthly)
  add('snagg', book.snagg.label, Math.max(0, Math.min(book.snagg.max, sel.snaggUsers)), book.snagg.perUserMonthly)
  for (const m of sel.modules) if (book.modules[m]) add(`module:${m}`, book.modules[m].label, 1, book.modules[m].monthly)
  for (const i of sel.integrations) if (book.integrations[i]) add(`integration:${i}`, `Integration: ${book.integrations[i].label}`, 1, book.integrations[i].monthly)

  const standardMonthly = r2(lines.reduce((a, l) => a + l.standardMonthly, 0))
  const monthlyTotal = r2(lines.reduce((a, l) => a + l.yourMonthly, 0))
  const savingPerMonth = r2(standardMonthly - monthlyTotal)
  const matrix = ([12, 24, 36] as const).map((tm) => {
    const d = book.discounts[String(tm)] || { monthly: 0, annual: 0 }
    const mTotal = r2(standardMonthly * (1 - d.monthly / 100))
    const aTotal = r2(standardMonthly * (1 - d.annual / 100))
    return { termMonths: tm, monthly: { pct: d.monthly, total: mTotal }, annual: { pct: d.annual, total: aTotal, perYear: r2(aTotal * 12) } }
  })
  const tr = book.proserv.training[sel.training] || book.proserv.training.basic || { label: '—', fee: 0 }
  return { lines, discountPct: pct, standardMonthly, monthlyTotal, billedAnnually: r2(monthlyTotal * 12), savingPerMonth, savingOverTerm: r2(savingPerMonth * sel.termMonths), training: { key: sel.training, label: tr.label, fee: tr.fee }, matrix }
}

const SYMBOL: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' }
export const moneyIn = (currency: string) => (n: number) => (SYMBOL[currency] || '£') + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
export const gbp = moneyIn('GBP')
